import { CheckInStatusEnum } from 'app/shared/enums/check-in-status-type.enum';
import { HttpBasicRequest } from 'app/shared/models/http-basic-request.model';
import { Shop } from 'app/shared/models/shop.model';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { ContactVM } from '../models/back-office-admin-common/contact.models';
import { LocationFullDTO } from '../../../shared/models/location.model';
import { PointOfSaleDetailVM } from '../models/point-of-sale/bo-point-of-sale.model';
import { OnlineStoreDto, OnlineStoreVM, StoreCustomerLocationGetDto } from '../models/point-of-sale/storePointOfSale.model';
import { AgencyMapper } from './agency-mapper';
import { PointOfSaleDto } from 'app/shared/models/point-of-sale.model';
import { RolesEnum } from 'app/shared/enums/roles.enum';
import { ActiveChannelDTO } from 'app/shared/models/channel.model';
import { CustomerFullDTO } from '../models/customer/customer.model';
import { LocationTypeEnum } from 'app/shared/enums/location-type.enum';
import { StorePosRequestModel } from '../models/point-of-sale/store-pos-request.model';

export class LocationMapper {

  public static PointOfSaleDetail(pointOfSale: PointOfSaleDto, contact: ContactVM[], storeList: Shop[], currentStoreId: number, customer?: CustomerFullDTO, mainRole?: RolesEnum): PointOfSaleDetailVM {
    const isFromBilling = customer && pointOfSale.location?.id === customer.customerLocations.find(x => x.locationType === LocationTypeEnum.BillingLocation)?.location?.id;
    return new PointOfSaleDetailVM({
      customerName: customer?.name,
      isFromBilling,
      customer,
      checkInStatus: pointOfSale.storePointOfSales?.find(x => x.store && x.store.id === currentStoreId)?.checkInStatus || CheckInStatusEnum.ToApprove,
      lastModificationTime: pointOfSale.lastModificationTime,
      lastBusinessUserModificationTime: pointOfSale.lastBusinessUserModificationTime,
      lastBusinessUserModifierId: pointOfSale.lastBusinessUserModifierId,
      creationTime: pointOfSale.creationTime,
      lastModifierId: pointOfSale.lastModifierId,
      creatorId: pointOfSale.creatorId,
      alternativeVat: pointOfSale.alternativeVat,
      alternativeGlnNumber: pointOfSale.alternativeGlnNumber,
      customerId: pointOfSale.customerId,
      externalId: pointOfSale.externalId,
      id: pointOfSale.id,
      location: pointOfSale.location,
      locationId: pointOfSale.locationId,
      name: pointOfSale.name,
      storePointOfSales: pointOfSale.storePointOfSales.filter(x => x.checkInStatus != CheckInStatusEnum.ToApprove).map(x => { x.storeName = x.store?.name; return x; }),
      onlineStores: storeList ? LocationMapper.OnlineStoreActiveMapper(pointOfSale.storePointOfSales, storeList, pointOfSale.activatedStoreChannels) : null,
      contacts: contact,
      activatedStoreChannels: pointOfSale.activatedStoreChannels,
      summerStartDay: pointOfSale.summerStartDay,
      summerStartMonth: pointOfSale.summerStartMonth,
      summerStockLimit: pointOfSale.summerStockLimit,
      winterStartDay: pointOfSale.winterStartDay,
      winterStartMonth: pointOfSale.winterStartMonth,
      winterStockLimit: pointOfSale.winterStockLimit,
      draftOrdersCount: pointOfSale.draftOrdersCount,
      useNameInOrderHeader: pointOfSale.useNameInOrderHeader,
      nameAndLocation: `${pointOfSale.name} - ${pointOfSale.location?.address1}, ${pointOfSale.location?.city}`
    });
  }

  public static OnlineStoreActiveMapper(onlineStore: OnlineStoreDto[], storeList: Shop[], activeChannels: ActiveChannelDTO[]): OnlineStoreVM[] {
    const activeStore: OnlineStoreVM[] = [];
    storeList.forEach(shop => {
      const activestore = onlineStore.find(shopActive => shopActive.store && shopActive.store.id === shop.id);
      activestore ?
        activeStore.push({
          store: shop,
          isPresentOnDB: true,
          checkInMessage: activestore?.checkInMessage,
          isEnabled: activestore.checkInStatus !== CheckInStatusEnum.Rejected && activestore.checkInStatus !== CheckInStatusEnum.ToApprove,
          storeName: shop.name,
          tierId: activestore.tierId,
          agencies: activestore.agencyStoreAssociations.map(agencyStore => AgencyMapper.AgencyListItemVM(agencyStore.agency, true, agencyStore)),
          storeId: shop.id,
          channels: activeChannels?.filter(c => c.storeId === shop.id).map(c => c.channel),
          checkInStatus: activestore.checkInStatus
        }) : activeStore.push({
          store: shop,
          checkInMessage: null,
          isPresentOnDB: false,
          isEnabled: false,
          storeName: shop.name,
          tierId: null,
          agencies: [],
          channels: [],
          storeId: shop.id,
          checkInStatus: CheckInStatusEnum.ToApprove
        });
    });
    return activeStore;
  }

  public static AddressOnlineStoreActiveMapper(onlineStore: StoreCustomerLocationGetDto[], storeList: Shop[]) {
    const activeStore: OnlineStoreVM[] = [];
    storeList.forEach(shop => {
      const activestore = onlineStore?.find(shopActive => shopActive.store && shopActive.store.id === shop.id);
      activestore ?
        activeStore.push({
          store: shop,
          checkInMessage: activestore.checkInMessage,
          isPresentOnDB: true,
          isEnabled: activestore.checkInStatus !== CheckInStatusEnum.Rejected && activestore.checkInStatus !== CheckInStatusEnum.ToApprove,
          storeName: shop.name,
          tierId: null,
          agencies: [],
          channels: [],
          storeId: shop.id,
          checkInStatus: activestore.checkInStatus
        },) : activeStore.push({
          store: shop,
          checkInMessage: null,
          isPresentOnDB: false,
          isEnabled: false,
          storeName: shop.name,
          tierId: null,
          agencies: [],
          channels: [],
          storeId: shop.id,
          checkInStatus: CheckInStatusEnum.ToApprove
        },);
    });
    return activeStore;
  }

  public static AddPointOfSale(customerId: number, locationId: number, posData: PointOfSaleDetailVM, baseUrl: string) {
    const request: HttpBasicRequest[] = [];
    request.push({
      url: baseUrl + `/point-of-sales`, method: 'post', body: {
        customerId,
        locationId,
        name: posData.name,
        alternativeVat: posData.alternativeVat,
        alternativeGlnNumber: posData.alternativeGlnNumber,
        summerStockLimit: posData.summerStockLimit,
        summerStartDay: posData.summerStartDay,
        summerStartMonth: posData.summerStartMonth,
        winterStockLimit: posData.winterStockLimit,
        winterStartDay: posData.winterStartDay,
        winterStartMonth: posData.winterStartMonth,
        useNameInOrderHeader: posData.useNameInOrderHeader
      }
    });
    return request;
  }

  public static AddStoreToPointOFSaleRequest(posId: number, onlineStores: OnlineStoreVM[], baseUrl: string, userRole: RolesEnum, myCheckinStatus: CheckInStatusEnum): HttpBasicRequest[] {
    const isAdmin = userRole <= RolesEnum.StoreUser;
    const request: HttpBasicRequest[] = [];
    onlineStores.filter(store => store.isPresentOnDB === false && store.isEnabled)
      .forEach(store => request.push({
        url: baseUrl + `/point-of-sales/${posId}/store`, method: 'post', body: {
          tierId: store.tierId,
          storeId: store.storeId,
          checkInStatus: store.checkInStatus === CheckInStatusEnum.ToApprove && isAdmin ? myCheckinStatus : store.checkInStatus,
          channels: store.channels.map(x => x.id)
        }
      }));
    return request;
  }

  public static StorePosAgenciesHandler(posId: number, currentOnlineStores: OnlineStoreVM[], cachedOnlineStores: OnlineStoreVM[], baseUrl: string): HttpBasicRequest[] {
    const requests: HttpBasicRequest[] = [];
    currentOnlineStores?.forEach(store => {

      const ex = cachedOnlineStores?.find(x => x.storeId == store.storeId);

      store.agencies.filter(ag => !ex || !ex.agencies?.some(exAg => exAg.id === ag.id)).forEach(ag => requests.push({
        url: baseUrl + `/point-of-sales/${posId}/agency-store/${ag.agencyStoreId}`, method: 'post',
      }));

      if (!!ex?.agencies?.length) ex.agencies.filter(oldAg => !store.agencies?.some(ag => ag.id == oldAg.id)).forEach(ag => requests.push({
        url: baseUrl + `/point-of-sales/${posId}/agency-store/${ag.agencyStoreId}`, method: 'delete',
      }));
    });
    return requests;
  }

  public static EditStoreToPointOFSaleRequest(posDataVM: PointOfSaleDetailVM, cachePosDataVm: PointOfSaleDetailVM, baseUrl: string, changeAddress: boolean = false, userRole: RolesEnum, myCheckinStatus: CheckInStatusEnum): HttpBasicRequest[] {
    const isAdmin = userRole <= RolesEnum.StoreUser;
    const request: HttpBasicRequest[] = [];
    posDataVM.onlineStores.filter(store => {
      const ex = cachePosDataVm.onlineStores.find(store2 => store.store.id === store2.store.id);
      return store.isPresentOnDB && !!ex && (changeAddress || !UtilityFunctions.isEquals(ex.agencies, store.agencies) || ex.isEnabled !== store.isEnabled || ex.tierId !== store.tierId || ex.channels !== store.channels);
    }).forEach(store => {
      const checkInStatus = store.isEnabled && isAdmin && !changeAddress && (store.checkInStatus === CheckInStatusEnum.ToApprove || store.checkInStatus === CheckInStatusEnum.Rejected) ? myCheckinStatus : (store.isEnabled && isAdmin && changeAddress) ? CheckInStatusEnum.OnHold : (changeAddress || !store.isEnabled) ? CheckInStatusEnum.ToApprove : store.checkInStatus;
      request.push({
        url: baseUrl + `/point-of-sales/${posDataVM.id}/store/${store.store.id}`,
        method: 'put',
        body: {
          tierId: store.tierId,
          checkInStatus,
          channels: store.channels.map(x => x.id)
        }
      });
    });
    return request;
  }

  public static PostStorePos(posDataVM: PointOfSaleDetailVM, cachePosDataVm: PointOfSaleDetailVM, baseUrl: string, changeSensistiveData: boolean = false, userRole: RolesEnum, myCheckinStatus: CheckInStatusEnum): HttpBasicRequest[] {
    const isAdmin = userRole <= RolesEnum.StoreUser;
    let result: HttpBasicRequest = null;
    const body = posDataVM.onlineStores.filter(store => {
      const ex = cachePosDataVm?.onlineStores?.find(store2 => store.store.id === store2.store.id);
      var result = (!!ex?.isPresentOnDB && (changeSensistiveData || !UtilityFunctions.isEquals(ex.agencies, store.agencies) || ex.isEnabled !== store.isEnabled || ex.tierId !== store.tierId || ex.channels !== store.channels)) || (!ex?.isPresentOnDB && store.isEnabled);
      return result;
    }).map(store => {
      const checkInStatus = store.isEnabled && isAdmin && !changeSensistiveData && (store.checkInStatus === CheckInStatusEnum.ToApprove || store.checkInStatus === CheckInStatusEnum.Rejected) ? myCheckinStatus : (store.isEnabled && isAdmin && changeSensistiveData) ? CheckInStatusEnum.OnHold : (changeSensistiveData || !store.isEnabled) ? CheckInStatusEnum.ToApprove : store.checkInStatus;
      return new StorePosRequestModel({
        channels: store.channels.map(x => x.id),
        checkInMessage: store.checkInMessage,
        checkInStatus: checkInStatus,
        storeId: store.store.id,
        tierId: store.tierId
      })
    });
    if (body?.length) {
      result = new HttpBasicRequest({
        method: 'post',
        url: baseUrl + `/point-of-sales/${posDataVM.id}/stores`,
        body
      });
    }
    return result ? [result] : [];
  }

  public static locationCreateRequest(customerLocation: LocationFullDTO, baseUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    customerLocation.countryId = customerLocation.country?.id;
    result.push({ url: baseUrl + `/locations`, method: 'post', body: customerLocation });
    return result;
  }

}
